import { Controller } from 'stimulus'

// Drives the roles editor: capability toggles, scope multi-select dropdowns,
// per-row exception chips, the status-transition grid, legacy-row removal,
// the live summary rail, and dirty-form tracking.
//
// Form contract: every potential permission row is a `.re-row-fields` span
// inside an element carrying `data-group-key`. Existing rows hold id +
// _destroy inputs; potential new rows hold record_type/action/scope inside a
// disabled <fieldset> (disabled controls are not submitted). Activating a row
// means _destroy=0 / enabling the fieldset; deactivating means the reverse.
export default class extends Controller {
  static targets = ['capRow', 'cards', 'summaryBody', 'filter',
    'statusGrid', 'statusPill', 'legacyRow',
    'statusExceptionTemplate', 'templateExceptionTemplate',
    'identityPanel', 'titleName', 'titleDesc', 'nameField', 'descField',
    'savebar', 'savebarSentinel']

  connect() {
    this.newIndex = 0
    this.dirty = false
    this.boundBeforeUnload = (event) => {
      // (an idle sign-out leaves without asking: session_timeout_controller)
      if (!this.dirty || window.limseySigningOut) return
      const unloadEvent = event
      unloadEvent.preventDefault()
      unloadEvent.returnValue = ''
    }
    this.boundCloseDropdowns = (event) => {
      if (event.target.closest('.re-dropdown, .re-pill, .re-caret')) return
      this.closeAllDropdowns()
    }
    this.boundMarkDirty = () => { this.dirty = true; this.refresh() }
    this.boundBeforeVisit = (event) => {
      if (this.dirty && !window.confirm('You have unsaved changes to this role. Leave without saving?')) {
        event.preventDefault()
      }
    }
    window.addEventListener('beforeunload', this.boundBeforeUnload)
    document.addEventListener('turbolinks:before-visit', this.boundBeforeVisit)
    document.addEventListener('click', this.boundCloseDropdowns)
    this.element.addEventListener('change', this.boundMarkDirty)
    this.element.addEventListener('submit', () => { this.dirty = false })
    this.observeSavebar()
    this.refresh()
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.boundBeforeUnload)
    document.removeEventListener('turbolinks:before-visit', this.boundBeforeVisit)
    document.removeEventListener('click', this.boundCloseDropdowns)
    if (this.savebarObserver) this.savebarObserver.disconnect()
  }

  // The bar is sticky; while its resting place (the sentinel just after it)
  // is off-screen the bar is floating over content and gets an elevation
  // shadow, which drops away as it settles into the card's bottom edge.
  observeSavebar() {
    if (!this.hasSavebarTarget || !this.hasSavebarSentinelTarget) return
    if (!('IntersectionObserver' in window)) return
    // The sentinel sits inside the bar's negative-margin overlap, so require
    // it to clear the bottom edge by a bar-height before dropping the shadow.
    this.savebarObserver = new IntersectionObserver((entries) => {
      this.savebarTarget.classList.toggle('re-floating', !entries[0].isIntersecting)
    }, { rootMargin: '0px 0px -24px 0px' })
    this.savebarObserver.observe(this.savebarSentinelTarget)
  }

  allowSubmit() { this.dirty = false }

  // ----- identity (card title <-> collapsed name/description fields) -----

  toggleIdentity(event) {
    event.preventDefault()
    this.identityPanelTarget.hidden = !this.identityPanelTarget.hidden
    if (!this.identityPanelTarget.hidden && this.hasNameFieldTarget) {
      const field = this.nameFieldTarget
      field.focus()
      field.setSelectionRange(field.value.length, field.value.length)
    }
  }

  syncIdentity() {
    if (this.hasTitleNameTarget && this.hasNameFieldTarget) {
      this.titleNameTarget.textContent = this.nameFieldTarget.value.trim() || 'New role'
    }
    if (this.hasTitleDescTarget && this.hasDescFieldTarget) {
      const description = this.descFieldTarget.value.trim()
      this.titleDescTarget.textContent = description ? `${description} · ` : ''
    }
  }

  // ----- row/group state -----

  rowFields(groupEl) {
    return Array.from(groupEl.querySelectorAll('.re-row-fields'))
  }

  rowActive(rowEl) {
    if (rowEl.dataset.existing === 'true') {
      return rowEl.querySelector('.re-destroy').value === '0'
    }
    const fieldset = rowEl.querySelector('fieldset')
    return !!fieldset && !fieldset.disabled
  }

  setRowActive(rowEl, active) {
    if (rowEl.dataset.existing === 'true') {
      const destroyField = rowEl.querySelector('.re-destroy')
      destroyField.value = active ? '0' : '1'
    } else {
      const fieldset = rowEl.querySelector('fieldset')
      if (fieldset) fieldset.disabled = !active
    }
    const groupEl = rowEl.closest('[data-group-key]')
    const key = rowEl.dataset.rowKey
    const excGroup = groupEl.querySelector(`.re-exc-group[data-row-key="${key}"]`)
    if (excGroup) excGroup.disabled = !active
    const scopeBox = groupEl.querySelector(`.re-dropdown input[data-row-key="${key}"]`)
    if (scopeBox) scopeBox.checked = active
  }

  groupActive(groupEl) {
    return this.rowFields(groupEl).some(row => this.rowActive(row))
  }

  // ----- actions -----

  toggleGroup(event) {
    const groupEl = event.target.closest('[data-group-key]')
    if (event.target.checked) {
      if (!this.groupActive(groupEl)) this.setRowActive(this.rowFields(groupEl)[0], true)
    } else {
      this.rowFields(groupEl).forEach(row => this.setRowActive(row, false))
    }
    this.dirty = true
    this.refresh()
  }

  // Scope checkboxes are fully independent — no auto-clearing. Checking "All"
  // alongside narrower scopes is redundant but harmless (union semantics), and
  // clearing selections as a side effect would break the check/uncheck round
  // trip and could discard per-row exception setups.
  toggleScope(event) {
    const groupEl = event.target.closest('[data-group-key]')
    const rowEl = groupEl.querySelector(`.re-row-fields[data-row-key="${event.target.dataset.rowKey}"]`)
    this.setRowActive(rowEl, event.target.checked)
    this.dirty = true
    this.refresh()
  }

  toggleChip(event) {
    const groupEl = event.target.closest('[data-group-key]')
    if (this.groupActive(groupEl)) {
      this.rowFields(groupEl).forEach(row => this.setRowActive(row, false))
    } else {
      this.setRowActive(this.rowFields(groupEl)[0], true)
    }
    this.dirty = true
    this.refresh()
  }

  toggleDropdown(event) {
    event.stopPropagation()
    const groupEl = event.target.closest('[data-group-key]')
    const dropdown = groupEl.querySelector('.re-dropdown')
    const wasHidden = dropdown.hidden
    this.closeAllDropdowns()
    dropdown.hidden = !wasHidden
  }

  closeAllDropdowns() {
    this.element.querySelectorAll('.re-dropdown').forEach((el) => {
      const dropdown = el
      dropdown.hidden = true
    })
  }

  toggleExceptions(event) {
    const row = event.target.closest('.re-row')
    const panel = row.querySelector('.re-exc-panel')
    panel.hidden = !panel.hidden
  }

  addException(event) {
    const { kind, key } = event.currentTarget.dataset
    const template = kind === 'status' ? this.statusExceptionTemplateTarget
      : this.templateExceptionTemplateTarget
    // Nested-attribute keys must be numeric for strong params; Date.now keeps
    // fresh chips clear of real exception ids.
    const chipIndex = Date.now() + this.newIndex
    this.newIndex += 1
    const html = template.innerHTML.replace(/__KEY__/g, key)
      .replace(/__IDX__/g, `${chipIndex}`)
    const line = event.currentTarget.closest('.re-exc-line')
    line.querySelector('[data-chips]').insertAdjacentHTML('beforeend', html)
    this.dirty = true
    this.refresh()
  }

  removeNewException(event) {
    event.target.closest('.re-xchip').remove()
    this.dirty = true
    this.refresh()
  }

  removeExistingException(event) {
    const chip = event.target.closest('.re-xchip')
    chip.querySelector('.re-destroy').value = '1'
    chip.hidden = true
    this.dirty = true
    this.refresh()
  }

  toggleLegacyRemove(event) {
    const row = event.target.closest('.re-legacy-row')
    const destroyField = row.querySelector('.re-destroy')
    const removing = destroyField.value === '0'
    destroyField.value = removing ? '1' : '0'
    row.classList.toggle('re-removed', removing)
    const button = event.target
    button.textContent = removing ? 'undo' : 'remove'
    this.dirty = true
    this.refresh()
  }

  toggleStatusGrid(event) {
    this.statusGridTarget.hidden = !this.statusGridTarget.hidden
    event.currentTarget.classList.toggle('open', !this.statusGridTarget.hidden)
  }

  jumpToArea(event) {
    event.preventDefault()
    const target = document.querySelector(event.currentTarget.getAttribute('href'))
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    this.element.querySelectorAll('.re-nav-item').forEach(el => el.classList.remove('active'))
    event.currentTarget.classList.add('active')
  }

  applyFilter() {
    const query = this.filterTarget.value.trim().toLowerCase()
    this.cardsTarget.querySelectorAll('.re-card').forEach((card) => {
      const cardEl = card
      let visible = 0
      cardEl.querySelectorAll('[data-cap-name]').forEach((row) => {
        const rowEl = row
        const match = query === '' || rowEl.dataset.capName.toLowerCase().includes(query)
        rowEl.hidden = !match
        if (match) visible += 1
      })
      cardEl.hidden = visible === 0
    })
  }

  refresh() {
    this.capRowTargets.forEach(groupEl => this.refreshGroup(groupEl))
    this.refreshNavCounts()
    this.refreshStatusPill()
    this.refreshCoherence()
    this.buildSummary()
  }

  // Warn (never block) when a write capability is granted without View for
  // the same record type. Record types with no View capability in the map
  // (API, Jobs - Samples, ...) are naturally exempt.
  refreshCoherence() {
    const groups = Array.from(this.element.querySelectorAll('[data-record-type][data-action-name]'))
    const readState = {}
    groups.forEach((el) => {
      if (el.dataset.actionName !== 'read') return
      readState[el.dataset.recordType] = { exists: true, active: this.groupActive(el) }
    })

    const warning = new Set()
    groups.forEach((el) => {
      const read = readState[el.dataset.recordType]
      if (el.dataset.actionName !== 'read' && read && read.exists && !read.active && this.groupActive(el)) {
        warning.add(el)
      }
    })

    this.coherenceWarnings = 0
    this.element.querySelectorAll('.re-warn-badge').forEach((badge) => {
      const badgeEl = badge
      const row = badgeEl.closest('.re-row')
      const rowWarns = warning.has(row)
        || Array.from(row.querySelectorAll('[data-record-type]')).some(el => warning.has(el))
      badgeEl.hidden = !rowWarns
      if (rowWarns) this.coherenceWarnings += 1
    })
  }

  // ----- rendering -----

  refreshGroup(groupEl) {
    const active = this.groupActive(groupEl)
    const isChip = groupEl.dataset.chip === 'true'

    if (isChip) {
      groupEl.querySelector('.re-chip').classList.toggle('on', active)
    } else {
      const toggle = groupEl.querySelector('.re-toggle-input')
      if (toggle) toggle.checked = active
    }

    const pillLabel = groupEl.querySelector('[data-pill-label]')
    if (pillLabel) {
      const labels = this.rowFields(groupEl)
        .filter(row => this.rowActive(row))
        .map(row => row.dataset.scopeLabel)
      if (labels.length === 0) {
        pillLabel.textContent = isChip ? '' : 'Off'
      } else {
        pillLabel.textContent = labels[0] + (labels.length > 1 ? ` +${labels.length - 1}` : '')
      }
    }

    const excCount = groupEl.querySelector('[data-exc-count]')
    if (excCount) {
      const count = this.visibleChips(groupEl).length
      excCount.textContent = count > 0 ? `${count} exception${count === 1 ? '' : 's'}` : 'exceptions'
      groupEl.querySelector('.re-exc-link').classList.toggle('has', count > 0)
    }

    groupEl.querySelectorAll('.re-exc-group').forEach((excGroup) => {
      const excGroupEl = excGroup
      const rowEl = groupEl.querySelector(`.re-row-fields[data-row-key="${excGroupEl.dataset.rowKey}"]`)
      if (rowEl) excGroupEl.disabled = !this.rowActive(rowEl)
    })
  }

  visibleChips(scopeEl) {
    return Array.from(scopeEl.querySelectorAll('.re-xchip'))
      .filter(chip => !chip.hidden && !chip.closest('fieldset[disabled]'))
  }

  refreshNavCounts() {
    this.cardsTarget.querySelectorAll('.re-card').forEach((card) => {
      let count = 0
      card.querySelectorAll('[data-group-key]').forEach((groupEl) => {
        if (this.groupActive(groupEl)) count += 1
      })
      card.querySelectorAll('.re-row:not([data-group-key]):not(.re-chip-row) .re-toggle-input:checked')
        .forEach(() => { count += 1 })
      const badge = this.element.querySelector(`[data-nav-count="${card.dataset.areaCard}"]`)
      if (badge) badge.textContent = count > 0 ? count : ''
    })
  }

  statusState() {
    const state = { involved: [], from: [], to: [], total: 0 }
    if (!this.hasStatusGridTarget) return state
    this.statusGridTarget.querySelectorAll('tbody tr').forEach((tr) => {
      state.total += 1
      const name = tr.querySelector('td').textContent.trim()
      const boxes = tr.querySelectorAll('input[type="checkbox"]')
      const from = boxes[0] && boxes[0].checked
      const to = boxes[1] && boxes[1].checked
      if (from) state.from.push(name)
      if (to) state.to.push(name)
      if (from || to) state.involved.push(name)
    })
    return state
  }

  refreshStatusPill() {
    if (!this.hasStatusPillTarget) return
    const state = this.statusState()
    const none = state.involved.length === 0
    this.statusPillTarget.textContent = none ? 'No status changes'
      : `${state.involved.length} of ${state.total} statuses`
    this.statusPillTarget.classList.toggle('re-pill-warn', none)
  }

  buildSummary() {
    if (!this.hasSummaryBodyTarget) return
    let html = ''

    this.cardsTarget.querySelectorAll('.re-card').forEach((card) => {
      const items = []
      card.querySelectorAll('.re-row').forEach((row) => {
        if (row.classList.contains('re-chip-row')) {
          const labels = []
          row.querySelectorAll('.re-chip-wrap').forEach((wrap) => {
            if (!this.groupActive(wrap)) return
            let label = wrap.querySelector('.re-chip span').textContent.trim()
            const pill = wrap.querySelector('[data-pill-label]')
            if (pill && pill.textContent) label += ` (${pill.textContent})`
            labels.push(label)
          })
          if (labels.length) items.push(`${row.dataset.capName}: ${labels.join(', ')}`)
        } else if (row.dataset.groupKey) {
          if (!this.groupActive(row)) return
          let label = row.dataset.capName
          const pill = row.querySelector('[data-pill-label]')
          if (pill && pill.textContent && pill.textContent !== 'Off') label += ` (${pill.textContent})`
          items.push(label)
        } else {
          const flag = row.querySelector('.re-toggle-input')
          if (flag && flag.checked) items.push(row.dataset.capName)
        }
      })
      if (items.length) {
        const title = card.querySelector('.re-card-title').textContent
        html += `<div class="re-sum-area"><h5>${this.escape(title)}</h5><ul>`
        html += items.map(item => `<li>${this.escape(item)}</li>`).join('')
        html += '</ul></div>'
      }
    })

    const statuses = this.statusState()
    if (statuses.from.length || statuses.to.length) {
      html += '<div class="re-sum-area"><h5>Job statuses</h5><ul>'
      if (statuses.from.length) html += `<li>Move jobs out of: ${this.escape(statuses.from.join(', '))}</li>`
      if (statuses.to.length) html += `<li>Move jobs into: ${this.escape(statuses.to.join(', '))}</li>`
      html += '</ul></div>'
    } else if (statuses.total > 0) {
      html += '<div class="re-sum-area"><h5>Job statuses</h5><ul><li>Cannot change job statuses</li></ul></div>'
    }

    const exceptionCount = this.visibleChips(this.element).length
    const legacyCount = this.legacyRowTargets
      .filter(row => row.querySelector('.re-destroy').value === '0').length
    const warnCount = this.coherenceWarnings || 0
    if (exceptionCount > 0 || legacyCount > 0 || warnCount > 0) {
      html += '<div class="re-sum-area"><h5>Fine print</h5><ul>'
      if (warnCount > 0) html += `<li class="re-sum-warn">${warnCount} granted without View access</li>`
      if (exceptionCount > 0) html += `<li>${exceptionCount} exception${exceptionCount === 1 ? '' : 's'}</li>`
      if (legacyCount > 0) html += `<li>${legacyCount} legacy row${legacyCount === 1 ? '' : 's'}</li>`
      html += '</ul></div>'
    }

    this.summaryBodyTarget.innerHTML = html === '' ? '<p class="re-sum-empty">Nothing yet &mdash; turn on a capability to build this role.</p>' : html
  }

  escape(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
